import { FC, useMemo, useState } from 'react';

import {
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';

import {
  getCouponShareStatusLabel,
  getCouponShareStatusStyle,
} from '../utils/giftOrderCoupon.utils';

interface CouponRowProps {
  coupon: GiftCouponForStore;
  onShare: (coupon: GiftCouponForStore) => void;
  onMemo: (coupon: GiftCouponForStore) => void;
  onAdditionalPhoneEdit: (couponId: string, phone: string) => Promise<void>;
  isSharing: boolean;
}

const formatConfirmedAt = (date: Date | string) => {
  const d = new Date(date);
  return `${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')} 완료`;
};

export const CouponRow: FC<CouponRowProps> = ({ coupon, onShare, onMemo, onAdditionalPhoneEdit, isSharing }) => {
  const giftItem = coupon.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;

  const imageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo]);

  const isShared = coupon.shareStatus !== GiftCouponShareStatus.NotShared;

  const isRedeemed =
    coupon.redeemStatus === GiftCouponRedeemStatus.Delivered ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Refunded ||
    coupon.redeemStatus === GiftCouponRedeemStatus.Canceled;

  const shareDisabled = isRedeemed || coupon.shareStatus === GiftCouponShareStatus.Viewed;

  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name : '';

  const isAdditional = !!coupon.isAdditional;
  const isReissued = !!coupon.isReissued;
  const needsCarefulShare = isAdditional || isReissued;

  const [localConfirmReset, setLocalConfirmReset] = useState(false);

  const effectiveConfirmedAt = localConfirmReset ? null : coupon.confirmedAt;

  const [recipientPhone, setRecipientPhone] = useState(
    coupon.additionalRecipientPhone || '',
  );
  const [recipientMemo, setRecipientMemo] = useState('');
  const [isSavingRecipient, setIsSavingRecipient] = useState(false);

  const handleSaveRecipientAndShare = async () => {
    const phone = recipientPhone.replace(/[^0-9]/g, '');
    if (!phone) {
      alert('수신자 휴대폰번호를 입력해주세요.');
      return;
    }
    setIsSavingRecipient(true);
    try {
      await onAdditionalPhoneEdit(coupon._id, phone);
      onShare(coupon);
    } catch {
      alert('수신자 정보 저장에 실패했습니다.');
    } finally {
      setIsSavingRecipient(false);
    }
  };

  return (
    <div
      className={`rounded-lg bg-white p-3 shadow-sm ${
        isShared ? 'ring-1 ring-green-400' : ''
      } ${needsCarefulShare ? 'bg-amber-50/30 ring-1 ring-amber-300' : ''}`}
    >
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
          {imageUrl ? (
            <img
              src={fileBucketUrl(imageUrl, 'w200')}
              alt={itemName || '쿠폰'}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
              No Image
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">#{coupon.seq}</span>
            <span className="text-xs text-gray-600">{coupon.code}</span>
            {needsCarefulShare && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                {isReissued ? '재발급' : '재발행'}
              </span>
            )}
            <span
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${getCouponShareStatusStyle(
                coupon.shareStatus,
              )}`}
            >
              {getCouponShareStatusLabel(coupon.shareStatus)}
            </span>
          </div>
          <p className="mt-0.5 truncate text-sm font-medium text-gray-800">{itemName}</p>
        </div>

        {isShared ? (
          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            {effectiveConfirmedAt ? (
              <>
                <button
                  onClick={() => onMemo(coupon)}
                  className="truncate rounded-lg bg-purple-50 px-3 py-1 text-xs font-medium text-purple-600 active:bg-purple-100"
                >
                  {coupon.memo || '메모 남기기'}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('혹시 확인을 실수로 누르셨나요?\n예를 누르면 다시 전달할 수 있습니다.')) {
                      setLocalConfirmReset(true);
                    }
                  }}
                  className="text-[10px] text-gray-400 underline decoration-dotted active:text-gray-600"
                >
                  {formatConfirmedAt(effectiveConfirmedAt)}
                </button>
              </>
            ) : (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => onMemo(coupon)}
                  className={`rounded-lg px-3 py-1 text-xs active:bg-purple-100 ${
                    coupon.memo
                      ? 'truncate bg-purple-50 font-medium text-purple-600'
                      : 'border border-dashed border-purple-300 text-purple-400'
                  }`}
                >
                  {coupon.memo || '메모 남기기'}
                </button>
                {!shareDisabled && (
                  <button
                    onClick={() => onShare(coupon)}
                    disabled={isSharing}
                    className={`flex-shrink-0 rounded-xl bg-green-100 px-3 py-1.5 text-xs font-bold text-green-700 active:bg-green-200 ${
                      isSharing ? 'opacity-50' : ''
                    }`}
                  >
                    {isSharing ? '...' : '다시 전달'}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          !shareDisabled &&
          !needsCarefulShare && (
            <button
              onClick={() => onShare(coupon)}
              disabled={isSharing}
              className={`flex-shrink-0 rounded-xl px-5 py-2.5 text-sm font-bold transition bg-purple-600 text-white active:bg-purple-700 ${
                isSharing ? 'opacity-50' : ''
              }`}
            >
              {isSharing ? '...' : '전달하기'}
            </button>
          )
        )}
      </div>

      {/* 재발행 쿠폰: 수신자 정보 입력 후 전달 */}
      {needsCarefulShare && !shareDisabled && !isShared && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="mb-2 text-[11px] text-amber-700">
            <span className="font-bold text-amber-900">휴대폰번호를 먼저 입력</span>한 뒤
            전달해주세요. 성함은 선택입니다.
          </p>
          <div className="flex flex-col gap-2">
            <input
              type="tel"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="받으시는 분 휴대폰번호"
              className="w-full rounded-lg border border-amber-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
              maxLength={13}
            />
            <input
              type="text"
              value={recipientMemo}
              onChange={(e) => setRecipientMemo(e.target.value)}
              placeholder="받으시는 분 성함 (선택)"
              className="w-full rounded-lg border border-amber-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
            <button
              onClick={handleSaveRecipientAndShare}
              disabled={isSavingRecipient}
              className="w-full rounded-xl bg-amber-600 py-2.5 text-sm font-bold text-white active:bg-amber-700 disabled:opacity-50"
            >
              {isSavingRecipient ? '저장 중...' : '정보 저장 후 전달하기'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
